import {
    ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef,
    ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faChevronDown, faChevronUp, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FormBuilderService } from "libraries/service/formBuilder/formBuilder.service";
import { HostAliasResource, IHostAlias } from "libraries/service/resources/host-alias/host-alias.resource";
import _get from 'lodash-es/get';
import _isString from 'lodash-es/isString';
import _set from 'lodash-es/set';
import _unset from 'lodash-es/unset';
import { ICheckboxFieldSpecs, IFieldSpecs } from '../../fields/interfaces/field-specs';
import { GenericForm } from '../generic-form';
import { segmentGroupInArray } from "../utils/groups";
import { IConfig } from './config';
import { FlexibilityService } from './services/flexibility.service';
import { DateService } from "libraries/date/date.service";
import { wenji } from "libraries/interfaces/IWenji";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { Yii, IYii } from "libraries/interfaces/Yii";
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { forkJoin } from 'rxjs';
import { SamResourceService } from 'libraries/service/resources/sam/samResource.service';
import { ISamAgent } from 'libraries/service/resources/sam/ISamAgent';
import {
    CabinClasses,
    FareTypes,
    Keywords,
    IPostAirSearchRequest,
    ISegment,
    DurationViaUnits
} from 'libraries/interfaces/i-post-air-search-request';
import { UserAuthService } from 'libraries/service/user-auth/user-auth.service';
import { switchMap, tap } from "rxjs/operators";

export interface IDropOption {
    label: string;
    value: string | number;
}

export interface IAirFormConfig {
    hosts: string[];
    selectedHosts: string[];
}

export const maxViasList: IDropOption[] = [
    { label: Yii.t('wui-shared-js', "Aucune préférence"), value: -1 },
    { label: Yii.t('wui-shared-js', "Vol direct uniquement"), value: 0 },
    { label: Yii.t('wui-shared-js', "1 escale"), value: 1 },
    { label: Yii.t('wui-shared-js', "2 escales"), value: 2 },
    { label: Yii.t('wui-shared-js', "3 escales"), value: 3 },
];

export const cabinClassesList: IDropOption[] = [
    { label: Yii.t('wui-shared-js', "Aucune préférence"), value: -1 },
    { label: Yii.t('wui-shared-js', "Économique"), value: CabinClasses.economy },
    { label: Yii.t('wui-shared-js', "Premium Economy"), value: CabinClasses.ecoPremium },
    { label: Yii.t('wui-shared-js', "Classe affaires"), value: CabinClasses.business },
    { label: Yii.t('wui-shared-js', "Première classe"), value: CabinClasses.first },
];

export const viaDurationUnitList: IDropOption[] = [
    { label: Yii.t('wui-shared-js', "Minute"), value: DurationViaUnits.minute },
    { label: Yii.t('wui-shared-js', "Heure"), value: DurationViaUnits.hour },
];

export const locatorsList: IDropOption[] = [];

export const fareTypeList: IDropOption[] = [
    { label: FareTypes.public, value: FareTypes.public },
    { label: FareTypes.negoFare, value: FareTypes.negoFare },
    { label: FareTypes.tourOperator, value: FareTypes.tourOperator }
];

export const keywordList: IDropOption[] = [
    { label: Yii.t('wui-shared-js', 'Bagages enregistrés'), value: Keywords.luggageInHold },
];

/**
 * Search-form
 * Generate a fieldset of needed fields
 * @extends GenericForm
 */
@Component({
    selector: 'searchAirTripForm',
    templateUrl: './searchAirTrip.component.html',
    styles: [`
        .moreCrit btnForm ::ng-deep button {
            padding-left: 0;
            margin-left: 0;
        }

        multiselect.h2-title ::ng-deep .field-label label{
            font-weight: bold;
            font-size: 1.1rem;
            margin-bottom: 7px;
            padding-bottom:0;
        }

        .flight-information {
            display: flex;
            width: 100%;
        }
    `],
})
export class SearchAirTripFormComponent extends GenericForm implements OnInit, OnChanges {
    public Yii: IYii = Yii;

    @Input()
    public config: IConfig;

    @Input()
    public searchData: any; // TODO create interface SearchData

    @Input()
    public newSearch: boolean = false;

    @Input()
    public readonlyParams: boolean = false;

    @Input()
    public isFromWatabi: boolean = false;
    /**
     * Every ViewChild is template
     * With this we give the possibilities to display the template we want dynamically
     * Every template (fieldset) as to have a view child reference
     */
    @ViewChild('quantity')
    public quantity: TemplateRef<HTMLElement>;

    @ViewChild('segment')
    public segment: TemplateRef<HTMLElement>;

    @ViewChild('carrier')
    public carrier: TemplateRef<HTMLElement>;

    @ViewChild('price')
    public price: TemplateRef<HTMLElement>;

    @ViewChild('keyword')
    public keyword: TemplateRef<HTMLElement>;

    @ViewChild('host')
    public host: TemplateRef<HTMLElement>;

    @ViewChild('flexibility')
    public flexibility: TemplateRef<HTMLElement>;

    @ViewChild('locator')
    public locator: TemplateRef<HTMLElement>;

    @ViewChild('maxVias')
    public maxVias: TemplateRef<HTMLElement>;

    @ViewChild('cabin')
    public cabin: TemplateRef<HTMLElement>;

    @ViewChild('lcc')
    public lcc: TemplateRef<HTMLElement>;

    public excludeLowCostFieldSpec: ICheckboxFieldSpecs;
    public flexibilityFieldSpec: IFieldSpecs;
    public flexibilityItems: IDropOption[] = [];

    public excludeLowCostEnabled: boolean = true;

    public locatorFieldSpec: IFieldSpecs;
    public locatorItems: IDropOption[] = locatorsList;

    public cabinFieldSpec: IFieldSpecs;
    public cabinItems: IDropOption[] = cabinClassesList;

    public maxViasFieldSpec: IFieldSpecs;
    public maxViasItems: IDropOption[] = maxViasList;

    public hostsList: IDropOption[] = [];
    public keywordList: IDropOption[] = keywordList;
    public fareTypeList: IDropOption[] = fareTypeList;

    public showMoreCrit: boolean;

    public iconDown: IconDefinition = faChevronDown;
    public iconUp: IconDefinition = faChevronUp;

    constructor(
        public fb: FormBuilder,
        public formBuilderService: FormBuilderService,
        protected flexibilityService: FlexibilityService,
        protected readonly dateService: DateService,
        protected readonly configurationResourceService: ConfigurationResourceService,
        protected readonly permissionResourceService: PermissionResourceService,
        protected readonly samResourceService: SamResourceService,
        protected readonly userAuthService: UserAuthService,
        protected readonly changeDetectorRef: ChangeDetectorRef,
        protected readonly hostAliasResource: HostAliasResource,
    ) {
        super(fb, formBuilderService);
    }

    public ngOnInit(): void {
        this.initForm();
        // Get locator items from permissions
        forkJoin([
            this.permissionResourceService.getPermissionsStartingWith(['iqba', 'profile']),
            this.samResourceService.getAgent(this.userAuthService.getUserId())
        ])
            .subscribe({
                next: ([permissions, contact]: [Set<string>, ISamAgent]): void => {
                    const locators: string[] = Array.from(permissions)
                        //TR/2021-02-09 : this following test removes permissions that equals to 'iqba', all needed permissions begins with 'iqba.profile.'
                        .filter((permission: string) => permission.includes('.'))
                        .map((permission: string): string => permission.split('.').pop())
                        .filter(Boolean);

                    const items: IDropOption[] = locators.map((locator: string): IDropOption => ({ value: locator, label: locator }));

                    this.locatorItems = [
                        ...locatorsList,
                        ...items
                    ];

                    if (contact.link && contact.link.defaultLocator && locators.includes(contact.link.defaultLocator)) {
                        this.form.get('locator').patchValue(contact.link.defaultLocator);
                    } else {
                        this.locatorItems = [
                            ...locatorsList
                        ];
                    }

                    if (this.searchData.locator !== -1) {
                        this.form.get('locator').patchValue(this.searchData.locator);
                    }
                    // I don't know why, but this call is necessary
                    // for the UI to be correctly updated after we modify the locators
                    this.changeDetectorRef.markForCheck();
                }
            });

        this.flexibilityFieldSpec = {
            formGroup: this.form.get(['flexibility', 'begin']) as FormGroup,
            title: Yii.t('wui-shared-js', 'Flexibilité des départs'),
            formControlName: 'plusOrMinus',
            placeholder: Yii.t('wui-shared-js', 'Flexibilité des départs'),
        };

        this.locatorFieldSpec = {
            formGroup: this.form,
            title: Yii.t('wui-shared-js', 'Agence'),
            formControlName: 'locator',
            placeholder: Yii.t('wui-shared-js', 'Aucune préférence')
        };

        this.maxViasFieldSpec = {
            formGroup: this.form,
            title: Yii.t('wui-shared-js', "Nombre d'escales"),
            formControlName: 'maxVias',
            placeholder: Yii.t('wui-shared-js', "Aucune préférence")
        };

        this.cabinFieldSpec = {
            formGroup: this.form,
            title: Yii.t('wui-shared-js', 'Classe cabine'),
            formControlName: 'cabin',
            placeholder: Yii.t('wui-shared-js', 'Aucune préférence')
        };

        this.excludeLowCostFieldSpec = {
            formGroup: this.form,
            title: Yii.t('wui-shared-js', 'Restriction'),
            checkboxLabel: Yii.t('wui-shared-js', 'hors LCC, Travelport uniquement'),
            formControlName: 'fullServiceCarriersOnly',
            placeholder: Yii.t('wui-shared-js', 'Aucune préférence')
        };
    }

    public reset(): void {
        super.reset();
        this.searchData = null;
        this.initValues();
    }

    /**
     * onSubmit call service to send request to API
     * @param value : values of fields, valid: a boolean after checking Validators
     */
    // #TODO Change any => IPostAirTripsSearchRequest
    public onSubmit(formOutput: { controls: any, value: any, valid: boolean }): void {
        if(this.readonlyParams){
            formOutput.value.host = formOutput.controls.host.value;
            formOutput.value.locator = formOutput.controls.locator.value.locator ? formOutput.controls.locator.value.locator : formOutput.controls.locator.value;
        }
        if (formOutput.valid) {
            const value: any = this.parseFormValue(formOutput);
            this.formBuilderService.emitSubmitForm(value);
        }
    }

    protected parseFormValue({ value, valid }: { value: any, valid: boolean }): any{
        this.removeEmpty(value);

        value.host = value.host.host;

        if (value.host.length === 1) {
            value.host = value.host[0];
        }
        //set keyword in array at root;
        if (value.keyword && value.keyword.keyword) {
            const keyword: string[] = _get(value, 'keyword.keyword');
            _unset(value, 'keyword');
            _set(value, 'keyword', keyword);
        }

        if (value.hasOwnProperty('cabin') && value.cabin !== -1) { moveValueToSegments(value, 'cabin'); }
        if (value.hasOwnProperty('maxVias') && value.maxVias > -1) { moveValueToSegments(value, 'maxVias'); }

        _unset(value, 'cabin');
        _unset(value, 'maxVias');

        if (!this.canDefineFlexibility()) {
            _unset(value, ["flexibility"]);
        }

        if (value.locator === -1) {
            _unset(value, 'locator');
        }

        if (value.segment) {
            value.segment = value.segment.map((formSegment: any) => {
                if (formSegment.fromAirportOnly) {
                    if (typeof formSegment.from === 'string') {
                        formSegment.from = {
                            airport: {
                                iata: formSegment.from,
                            }
                        };
                    }
                } else {

                    if (formSegment.from && formSegment.from.airport && typeof formSegment.from !== 'string') {
                        formSegment.from = formSegment.from.airport.iata;
                    }
                }
                if (formSegment.toAirportOnly) {
                    if (typeof formSegment.to === 'string') {
                        formSegment.to = {
                            airport: {
                                iata: formSegment.to,
                            }
                        };
                    }
                } else {

                    if (formSegment.to && formSegment.to.airport && typeof formSegment.to !== 'string') {
                        formSegment.to = formSegment.to.airport.iata;
                    }
                }

                delete formSegment.fromAirportOnly;
                delete formSegment.toAirportOnly;
                return formSegment;
            });

            if (value.segment.length === 2) {
                const firstFromValue: string = value.segment[0].from.airport ? value.segment[0].from.airport.iata : value.segment[0].from;
                const firstToValue: string = value.segment[0].to.airport ? value.segment[0].to.airport.iata : value.segment[0].to;
                const secondFromValue: string = value.segment[1].from.airport ? value.segment[1].from.airport.iata : value.segment[1].from;
                const secondToValue: string = value.segment[1].to.airport ? value.segment[1].to.airport.iata : value.segment[1].to;
                if (firstFromValue === secondToValue && firstToValue === secondFromValue) {
                    value.segment[1] = {
                        ...value.segment[1],
                        from: value.segment[0].to,
                        to: value.segment[0].from,
                    };
                }
            }
        }
        return value;
    }

    public canShow(name: string): boolean {
        return this.config && this.config[name] && this.config[name].show;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const newSearchData: IPostAirSearchRequest = _get(changes, ['searchData', 'currentValue']);
        if (newSearchData) {
            if (!this.form) {
                this.initForm();
            }
            this.initValues();
        }
    }

    public canDefineFlexibility(): boolean {
        return this.config.segment.direction !== "Multiple";
    }

    public getHostConfig(): IFieldSpecs {
        return {
            formGroup: this.form.get(['host']) as FormGroup,
            formControlName: 'host',
            label: Yii.t('wui-shared-js', 'Sources'),
            validators: [{
                validator: Validators.required,
                errors: {
                    required: {
                        when: "touched",
                        message: Yii.t('wui-shared-js', 'Source requise'),
                    }
                }
            }],
            disabled: this.readonlyParams
        };
    }
    public getKeywordConfig(): IFieldSpecs {
        return {
            formGroup: this.form.get(['keyword']) as FormGroup,
            formControlName: 'keyword',
            label: Yii.t('wui-shared-js', 'Services inclus'),
        };
    }
    public getFareTypeConfig(): IFieldSpecs {
        return {
            formGroup: this.form.get(['price']) as FormGroup,
            formControlName: 'fareType',
            label: Yii.t('wui-shared-js', 'Types de Tarifs'),
        };
    }

    public handleHostChange(hosts: string[]): void {
        if (hosts.indexOf('TRAVELPORT') !== -1) {
            this.excludeLowCostEnabled = true;
            return;
        }

        this.excludeLowCostEnabled = false;
    }

    private initForm(): void {
        if (!this.form) {
            this.form = this.fb.group({
                ...this.constructForm()
            });

            this.initValues();
        }
    }

    protected initValues(): void {
        if (!this.searchData) {
            this.searchData = {};
        }

        this.searchData = {
            ...this.searchData,
            ...this.searchData.body
        };

        if (this.searchData.segment) {
            if (this.searchData.segment.length > 0) {
                //Dates in proper JS format
                this.searchData.segment.forEach((seg) => {
                    if (_isString(seg.begin)) {
                        seg.begin = this.dateService.getStr2JsDate(seg.begin);
                    }else if( seg.begin?.value){
                        seg.begin.value = this.dateService.getStr2JsDate(seg.begin.value);
                    }

                    // Set cabin, maxVias to the root, to patch the value in the form
                    // If value doesn't exist set -1 (default value)
                    this.searchData.cabin = seg.cabin ? seg.cabin : -1;
                    this.searchData.maxVias = seg.maxVias > -1 ? seg.maxVias : -1;
                });
            }

            // prepare form to get the proper quantity of segments
            const segment = this.form.get('segment') as FormArray;
            const qttSearch: number = this.searchData.segment.length;
            const qttForm: number = segment.value.length;
            for (let i: number = qttForm - 1; i >= 0; i--) {
                segment.removeAt(i);
            }
            for (let i: number = 0; i < qttSearch; i++) {
                segment.push(segmentGroupInArray(this.fb));
            }
        } else {
            // Patch default value
            this.searchData.cabin = -1;
            this.searchData.maxVias = -1;
        }

        this.searchData.locator = this.searchData.locator ? this.searchData.locator : -1;

        this.configurationResourceService
            .get(['wui', 'app_locobook', 'config', 'forms', 'form_locobook.json'], {})
            .pipe(
                tap((formConfig: IAirFormConfig) => {
                    if (!this.searchData.host) {
                        this.form.get(['host', 'host']).patchValue(formConfig.selectedHosts);
                    } else {
                        this.form.get(['host', 'host']).patchValue(this.searchData.host);
                    }
                }),
                switchMap((formConfig: IAirFormConfig) => this.hostAliasResource.getAliases(formConfig.hosts))
            )
            .subscribe((hostAliases: IHostAlias[]) => {
                this.hostsList = transformHostsResponseToLabelValue(hostAliases);
                this.changeDetectorRef.markForCheck();
            });

        this.flexibilityItems = this.flexibilityService
            .getFlexibilityOptions(wenji.locobook.search.departuresFlexibility);
        this.form.get(['flexibility', 'begin', 'plusOrMinus']).patchValue(0);
        this.searchData.quantity = this.searchData.quantity || { adult: 1, child: 0, infant: 0 };
        this.searchData.carrier = this.searchData.carrier || {};

        this.form.get(['keyword', 'keyword']).patchValue(this.searchData.keyword ? this.searchData.keyword : '');

        if(this.readonlyParams){
            this.form.get('locator').disable();
            this.form.get('fullServiceCarriersOnly').disable();
        }

        this.form.patchValue(this.searchData);
    }
}

function transformHostsResponseToLabelValue(hostAliases: IHostAlias[]): Array<{ label: string, value: string }> {
    return (hostAliases || []).map((hostAlias: IHostAlias) => {
        return { label: hostAlias.alias, value: hostAlias.host };
    });
}

function moveValueToSegments(values: IPostAirSearchRequest, property: string): void {
    values.segment.forEach((segment: ISegment) => _set(segment, property, values[property]));
    _unset(values, property);
}
